import {
    CreateParams, CreateResult,
    DataProvider,
    DeleteManyParams, DeleteManyResult,
    DeleteParams, DeleteResult,
    GetListParams, GetListResult,
    GetManyParams,
    GetManyReferenceParams, GetManyReferenceResult, GetManyResult,
    GetOneParams, GetOneResult,
    HttpError,
    UpdateManyParams,
    UpdateManyResult,
    UpdateParams, UpdateResult
} from "react-admin";
import {
    read_cookie,
} from 'sfcookies';
import { apiUrl, UseUrlManagerCleanUrl, hostUrl } from "../components/utils/url-manager";
import { AdminUser, NokWebsite } from "../types/entities";
import {addSortToUrl, cleanEntityString, createUploadFormData, processGetManyByReferenceFilter, readJsonOrRefuse, refuseOnHttpError, restoreEntityString} from "./provider/defaults";

type AdminUserPayload = {
    id: string,
    email: string,
    roles: string,
    avatar: string,
    password: string,
    name: string
}

function cleanParams(data: any[]) : AdminUser {
    let image: any = undefined;
    if (data['avatar'] !== undefined) {
        image = {
            src: hostUrl + data['avatar'],
            title: data['avatar'].substring(data['avatar'].lastIndexOf('/') + 1)
        }
    }
    // Joined with a separator rather than concatenated. This used to build `ROLE_ADMINROLE_USER`
    // out of two roles, which nothing noticed because the column is read only and single valued in
    // practice. It is shown on the list as a record of pre-ACL access and nothing authorises on it.
    const roles: string = Array.isArray(data['roles']) ? data['roles'].join(', ') : '';

    return {
        id: data['id'],
        name: data['name'],
        email: data['email'],
        roles: roles,
        avatar: image,
        // Carried through deliberately. This function returns a fixed shape, so a field it does
        // not name is dropped, and the ACL role select would then render empty on every edit and
        // submit that emptiness back - clearing the role of anyone whose name was corrected.
        role: cleanEntityString(data['role'], '/hq_api/admin_roles/'),
    }
}

/**
 * Admin User Data Provider
 * @type {{deleteMany: (function(*, *): Promise<never>), updateMany: (function(*, *): Promise<never>), updateUserProfile: adminUserDataProvider.updateUserProfile, getList: (function(*, *): Promise<{total: number, data: *[]}>), getMany: (function(*, *): Promise<never>), getManyReference: (function(*, *): Promise<never>), getOne: (function(*, *): Promise<{data: {}}>), create: (function(*, *): Promise<{data: {}}>), update: (function(*, *): Promise<*>), getProfileData: ((function(*): (Promise<Awaited<{data: any}>>))|*), delete: (function(*, *): Promise<never>)}}
 */
const adminUserDataProvider: DataProvider  = {
    // get a list of records based on sort, filter, and pagination
    getList:    async (resource: string, params: GetListParams): Promise<GetListResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += '?page=' + params.pagination.page + '&itemsPerPage=' + params.pagination.perPage;
        }
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url : url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<any> => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any): { data: AdminUser[], total: number } => {
                let responseData: { data: AdminUser[], total: number } = {
                    data: [],
                    total: 0
                };
                if (Array.isArray(data['hydra:member'])) {
                    let users: AdminUserPayload[] = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for (let i: number = 0; i < users.length; i++) {
                        let user: any = users[i];
                        responseData.data.push(cleanParams(user));
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error): any => {
                console.log('User');
                console.log(error);
                return Promise.reject(error);
            });
        //
    },
    // get a single record by id
    getOne:     async (resource: string, params: GetOneParams): Promise<GetOneResult> => {
        // debugger;
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource + '/' + params.id;
        url = UseUrlManagerCleanUrl({ url : url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<any> => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any): { data: {} } => {
                let responseData: { data: {} } = {
                    data: {}
                };
                if (data['id']) {
                    responseData.data = cleanParams(data);
                }
                return responseData;
            })
            .catch( (error: Error ): Promise<GetOneResult> => {
                console.log('User');
                console.log(error);
                return Promise.reject(error);
            });
    },
    // get a list of records based on an array of ids
    getMany:    (resource: string, params: GetManyParams): Promise<GetManyResult> => {
        return Promise.reject();
    },
    // get the records referenced to another record, e.g. comments for a post
    getManyReference: (resource: string, params: GetManyReferenceParams): Promise<GetManyReferenceResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += '?page=' + params.pagination.page + '&itemsPerPage=' + params.pagination.perPage;
        }
        url = processGetManyByReferenceFilter(url, params);
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: AdminUser[], total: number } = {
                    data: [],
                    total: 0
                };

                if (Array.isArray(data['hydra:member'])) {
                    let operators: any[] = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for(let i: number = 0; i < operators.length; i++) {
                        let operator: any[] = operators[i];
                        responseData.data.push(cleanParams(operator));
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error)
            });
    },
    // create a record
    create:  async   (resource: string, params: CreateParams): Promise<CreateResult> => {
        let token: string = read_cookie('auth');
        // No `roles` transform since Stage 5. The legacy column is out of HQ-API's write group,
        // so sending it does nothing, and the form no longer offers it.
        params.data.role = restoreEntityString(params.data.role, '/hq_api/admin_roles/');
        let url: string = apiUrl + resource;
        const request: Request = new Request(url, {
            method: 'POST',
            body: JSON.stringify(params['data']),
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<any> => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any): { data: {} } => {
                let responseData: { data: {} } = {
                    data: {}
                };
                if (data['id']) {
                    responseData.data = cleanParams(data);
                }
                return responseData;
            })
            .catch( (error: Error): Promise<any> => {
                console.log('User');
                console.log(error);
                return Promise.reject(error);
            });

    },
    // update a record based on a patch
    update: async (resource: string, params: UpdateParams): Promise<UpdateResult> => {
        // No `roles` transform since Stage 5, as in `create` above.
        params.data.role = restoreEntityString(params.data.role, '/hq_api/admin_roles/');
        let token: string = read_cookie('auth');
        let url: string = UseUrlManagerCleanUrl({ url: apiUrl + resource + '/' + params.id });

        if (params.data?.avatar?.rawFile) {
            const uploadData: { video?: string, image?: string, error?:string } = await createUploadFormData(
                params, { image: 'avatar', video: 'video' },
                'admin_users'
            );
            if (uploadData.error !== undefined) {
                throw Error(uploadData.error);
            }
            params.data.avatar = uploadData?.image ?? undefined;
        } else {
            params.data.avatar = params.data.avatar?.src.replace(hostUrl, '') ?? undefined;
        }

        const request: Request = new Request(url, {
            method: 'PATCH',
            body: JSON.stringify(params['data']),
            headers: new Headers({
                'Content-Type' : 'application/merge-patch+json',
                'token' : token,
                'hq': 'hq'
            })

        });
        return fetch(request)
            .then( async (response: Response): Promise<any> => {
                const body: any = await response.json().catch((): any => ({}));

                // HQAPI-73. A refused password change answers 422 with the reason in
                // hydra:description, and this used to reject with nothing at all - so
                // AdminUserEdit's `error.message` was undefined and the admin was told
                // "Failed to update error: undefined" whatever had gone wrong.
                if (!response.ok) {
                    const reason: string = body?.['hydra:description']
                        ?? body?.detail
                        ?? body?.['hydra:title']
                        ?? response.statusText;
                    return Promise.reject(new HttpError(reason, response.status, body));
                }

                return body;
            })
            .then( (data: any): { data: {} } | Promise<any> => {
                let responseData: { data: {} } = {
                    data: {}
                };

                if (data['id']) {
                    responseData.data = cleanParams(data);
                }
                if (responseData.data['id']) {
                    return responseData;
                } else {
                    return Promise.reject(new HttpError('The API answered without a user record', 200, data));
                }
            })
            .catch( (error: Error): Promise<any> => {
                console.log('User');
                console.log(error);
                // Rejecting with the error rather than with nothing: what the notification
                // shows is this message.
                return Promise.reject(error);
            });
    },
    // update a list of records based on an array of ids and a common patch
    updateMany: async (resource: string, params:UpdateManyParams): Promise<UpdateManyResult> => {
        return Promise.reject();
    },
    // delete a record by id
    delete: async (resource: string, params: DeleteParams) : Promise<DeleteResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource + '/' + params.id;
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'DELETE',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<{ data: any }> => {
                return refuseOnHttpError(response)
                    .then((): { data: any } => ({ data: params }));
            })
            .catch( (error: Error): Promise<any> => {
                console.log(error);
                return Promise.reject(error);
            });
    },
    // delete a list of records based on an array of ids
    deleteMany: async (resource: string, params: DeleteManyParams): Promise<DeleteManyResult> => {
        return Promise.reject();
    },
    getProfileData: async (params: any): Promise<{ data: any }> => {
        const storedProfile: string = localStorage.getItem("profile");

        if (storedProfile) {
            return Promise.resolve({
                data: JSON.parse(storedProfile),
            });
        }
        return Promise.reject({
            redirect: 'admin_users'
        });
    }
}



/**
 * Convert image blob to base64 for send to API
 * @param file
 * @returns {Promise<unknown>}
 */
function convertFileToBase64(file: any): Promise<any> {

    if (file === undefined || file === null || typeof file.src === "undefined" || file === "" || file.src === "" || file.src === null) {
        return new Promise((resolve: (value: any) => void, reject: (reason?: any) => void): void => {
            resolve('');
        });
    } else if (typeof file.src !== "undefined" && file?.src.includes('/images/admin/avatar')) {
        file.src = file.src.replace(hostUrl, '');
        return new Promise((resolve: (value: any) => void, reject: (reason?: any) => void): void => {
            resolve(file.src);
        });
    } else {
        return new Promise((resolve: (value: any) => void, reject: (reason?: any) => void): void => {
            const reader: FileReader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file.rawFile);
        });
    }
}

export default adminUserDataProvider;